import { useState } from 'react';
import { useCreateCourse, useUpdateCourse } from '@/api/course-builder';
import { FormShell } from '@/components/shared/form-shell';
import { FileUpload } from '@/components/shared/file-upload';
import type {
  BuilderCourse,
  BuilderCourseCategory,
  DifficultyLevel,
  CreateCourseInput,
  UpdateCourseInput,
} from '@/types';
import { inputClassName, labelClassName } from './constants';
import { CourseSettingsFields } from './course-settings-fields';
import { CourseTextFields } from './course-text-fields';
import { fileNameFromUrl } from '@/lib/file-name';

type CourseFormDialogProps = {
  course?: BuilderCourse | null;
  onClose: () => void;
};

export function CourseFormDialog({ course, onClose }: CourseFormDialogProps) {
  const isEdit = !!course;
  const createMut = useCreateCourse();
  const updateMut = useUpdateCourse();
  const mutation = isEdit ? updateMut : createMut;

  const [title, setTitle] = useState(course?.title ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [category, setCategory] = useState<BuilderCourseCategory | ''>(course?.category ?? '');
  const [isRequired, setIsRequired] = useState(course?.is_required ?? true);
  const [passingGrade, setPassingGrade] = useState(course?.passing_grade ?? 70);
  const [maxAttempts, setMaxAttempts] = useState(course?.max_attempts ?? 3);
  const [sequentialUnlock, setSequentialUnlock] = useState(course?.sequential_unlock ?? true);
  const [durationValue, setDurationValue] = useState<number | ''>(course?.duration_value ?? '');
  const [durationUnit, setDurationUnit] = useState(course?.duration_unit ?? 'days');
  const [estimatedHours, setEstimatedHours] = useState<number | ''>(course?.estimated_hours ?? '');
  const [difficultyLevel, setDifficultyLevel] = useState<DifficultyLevel | ''>(
    course?.difficulty_level ?? '',
  );
  const [learningObjectives, setLearningObjectives] = useState(course?.learning_objectives ?? '');
  const [complianceTags, setComplianceTags] = useState(course?.compliance_tags ?? '');
  const [trainingValidity, setTrainingValidity] = useState<number | ''>(
    course?.training_validity_months ?? '',
  );
  const [minVideoPercent, setMinVideoPercent] = useState<number | ''>(
    course?.min_video_percent ?? '',
  );
  const [thumbnail, setThumbnail] = useState<{
    url: string;
    name: string;
    size: number;
    type: string;
  } | null>(
    course?.thumbnail_url
      ? {
          url: course.thumbnail_url,
          name: fileNameFromUrl(course.thumbnail_url),
          size: 0,
          type: '',
        }
      : null,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    try {
      if (isEdit && course) {
        const data: UpdateCourseInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          category: (category as BuilderCourseCategory) || undefined,
          is_required: isRequired,
          passing_grade: passingGrade,
          max_attempts: maxAttempts,
          sequential_unlock: sequentialUnlock,
          duration_value: durationValue || undefined,
          duration_unit: durationValue ? (durationUnit as 'days' | 'weeks' | 'months') : undefined,
          estimated_hours: estimatedHours || undefined,
          difficulty_level: (difficultyLevel as DifficultyLevel) || undefined,
          learning_objectives: learningObjectives.trim() || undefined,
          compliance_tags: complianceTags.trim() || undefined,
          training_validity_months: trainingValidity || undefined,
          min_video_percent: minVideoPercent || undefined,
          thumbnail_url: thumbnail?.url ?? undefined,
        };
        await updateMut.mutateAsync({ id: course.id, data });
      } else {
        const data: CreateCourseInput = {
          title: title.trim(),
          ...(description.trim() && { description: description.trim() }),
          ...(category && { category: category as BuilderCourseCategory }),
          is_required: isRequired,
          passing_grade: passingGrade,
          max_attempts: maxAttempts,
          sequential_unlock: sequentialUnlock,
          ...(durationValue && { duration_value: Number(durationValue) }),
          ...(durationValue &&
            durationUnit && { duration_unit: durationUnit as 'days' | 'weeks' | 'months' }),
          ...(estimatedHours && { estimated_hours: Number(estimatedHours) }),
          ...(difficultyLevel && { difficulty_level: difficultyLevel as DifficultyLevel }),
          ...(learningObjectives.trim() && { learning_objectives: learningObjectives.trim() }),
          ...(complianceTags.trim() && { compliance_tags: complianceTags.trim() }),
          ...(trainingValidity && { training_validity_months: Number(trainingValidity) }),
          ...(minVideoPercent && { min_video_percent: Number(minVideoPercent) }),
          ...(thumbnail?.url && { thumbnail_url: thumbnail.url }),
        };
        await createMut.mutateAsync(data);
      }
      onClose();
    } catch {
      // Error handled by TanStack Query
    }
  };

  return (
    <FormShell
      title={isEdit ? 'Editar capacitación' : 'Nueva capacitación'}
      onClose={onClose}
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label htmlFor="course-title" className={labelClassName}>
            Título *
          </label>
          <input
            id="course-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClassName}
            placeholder="Nombre de la capacitación"
            maxLength={300}
            required
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="course-desc" className={labelClassName}>
            Descripción
          </label>
          <textarea
            id="course-desc"
            value={description}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            className={inputClassName}
            placeholder="Descripción de la capacitación..."
            maxLength={5000}
          />
        </div>

        <CourseSettingsFields
          category={category}
          setCategory={setCategory}
          difficultyLevel={difficultyLevel}
          setDifficultyLevel={setDifficultyLevel}
          passingGrade={passingGrade}
          setPassingGrade={setPassingGrade}
          maxAttempts={maxAttempts}
          setMaxAttempts={setMaxAttempts}
          durationValue={durationValue}
          setDurationValue={setDurationValue}
          durationUnit={durationUnit}
          setDurationUnit={setDurationUnit}
          estimatedHours={estimatedHours}
          setEstimatedHours={setEstimatedHours}
          trainingValidity={trainingValidity}
          setTrainingValidity={setTrainingValidity}
          minVideoPercent={minVideoPercent}
          setMinVideoPercent={setMinVideoPercent}
          isRequired={isRequired}
          setIsRequired={setIsRequired}
          sequentialUnlock={sequentialUnlock}
          setSequentialUnlock={setSequentialUnlock}
        />

        <CourseTextFields
          learningObjectives={learningObjectives}
          setLearningObjectives={setLearningObjectives}
          complianceTags={complianceTags}
          setComplianceTags={setComplianceTags}
        />

        {/* Thumbnail */}
        <FileUpload
          folder="thumbnails"
          accept=".jpg,.jpeg,.png,.webp"
          maxSizeMB={5}
          label="Miniatura"
          value={thumbnail}
          onChange={setThumbnail}
          disabled={mutation.isPending}
        />

        {mutation.error && <p className="text-xs text-destructive">{mutation.error.message}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {mutation.isPending
              ? 'Guardando...'
              : isEdit
                ? 'Guardar cambios'
                : 'Crear capacitación'}
          </button>
        </div>
      </form>
    </FormShell>
  );
}
